// src/pages/Godown/GodownManagerDashboard.jsx
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import orderService from '../../services/order.service';
import paymentService from '../../services/payment.service';
import Toast from '../../components/Toast/Toast';
import DispatchModal from './DispatchModal';
import './GodownManagerDashboard.css';
import authService from '../../services/auth.service';


const STATUS_TABS = [
  { key: 'ALL', label: 'All' },
  { key: 'PENDING_CONFIRMATION', label: 'Pending' },
  { key: 'CONFIRMED', label: 'Confirmed' },
  { key: 'PARTIALLY_DISPATCHED', label: 'Partial' },
  { key: 'DISPATCHED', label: 'Dispatched' },
  { key: 'DELIVERED', label: 'Delivered' },
  { key: 'FAILED_TO_DELIVER', label: 'Failed' },
  { key: 'CANCELLED', label: 'Cancelled' },
];

const STATUS_COLORS = {
  PENDING_CONFIRMATION: { bg: '#fff8e1', color: '#f57f17' },
  CONFIRMED: { bg: '#e8f5e9', color: '#2e7d32' },
  REJECTED: { bg: '#fce4ec', color: '#c62828' },
  PARTIALLY_DISPATCHED: { bg: '#fff3e0', color: '#e65100' },
  DISPATCHED: { bg: '#e3f2fd', color: '#1565c0' },
  DELIVERED: { bg: '#e8f5e9', color: '#1b5e20' },
  FAILED_TO_DELIVER: { bg: '#fce4ec', color: '#c62828' },
  CANCELLED: { bg: '#f5f5f5', color: '#757575' },
  DRAFT: { bg: '#f8f9fa', color: '#495057' },
};

const PAYMENT_MODES = [
  { value: 'CASH', label: '💵 Cash' },
  { value: 'CHEQUE', label: '📄 Cheque' },
  { value: 'UPI', label: '📱 UPI' },
  { value: 'NEFT', label: '🏦 NEFT' },
  { value: 'RTGS', label: '🏦 RTGS' },
  { value: 'IMPS', label: '🏦 IMPS' },
];

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const formatAmount = (a) => `₹${Number(a || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

// ── Order Detail Modal ────────────────────────────────────────────────────────
const OrderDetailModal = ({ order, onClose }) => (
  <div className="gdm-overlay" onClick={onClose}>
    <div className="gdm-modal" style={{ maxWidth: 700 }} onClick={e => e.stopPropagation()}>
      <div className="gdm-modal-header">
        <div>
          <h5 className="gdm-modal-title">Order Details — {order.orderId}</h5>
          <p className="gdm-modal-sub">{formatDate(order.orderDate)} · {order.dealer?.dealerShopName}</p>
        </div>
        <button className="gdm-close-btn" onClick={onClose}>✕</button>
      </div>
      <div className="gdm-modal-body">
        <div style={{ marginBottom: 16 }}>
          <span className="gdm-status-badge"
            style={{ background: STATUS_COLORS[order.orderStatus]?.bg, color: STATUS_COLORS[order.orderStatus]?.color }}>
            {order.orderStatus?.replace(/_/g, ' ')}
          </span>
        </div>
        <div className="gdm-detail-grid">
          <div className="gdm-detail-item">
            <span className="gdm-detail-label">Dealer</span>
            <span className="gdm-detail-value">
              {order.dealer?.dealerShopName || '—'}<br />
              {order.dealer?.tahsil || ''}<br />
              {order.dealer?.district || ''}<br />
              {order.dealer?.state || ''}
            </span>
          </div>
          <div className="gdm-detail-item">
            <span className="gdm-detail-label">Company</span>
            <span className="gdm-detail-value">{order.dealer?.company?.name || '—'}</span>
          </div>
          <div className="gdm-detail-item">
            <span className="gdm-detail-label">Owner</span>
            <span className="gdm-detail-value">{order.dealer?.ownerName || '—'}</span>
          </div>
          <div className="gdm-detail-item">
            <span className="gdm-detail-label">Order Date</span>
            <span className="gdm-detail-value">{formatDate(order.orderDate)}</span>
          </div>
          <div className="gdm-detail-item">
            <span className="gdm-detail-label">Total Amount</span>
            <span className="gdm-detail-value" style={{ color: '#2e7d32', fontWeight: 700 }}>
              {formatAmount(order.totalAmount)}
            </span>
          </div>
          {order.transportVia && (
            <div className="gdm-detail-item">
              <span className="gdm-detail-label">Transport</span>
              <span className="gdm-detail-value">{order.transportVia?.replace(/_/g, ' ')}</span>
            </div>
          )}
          {order.notes && (
            <div className="gdm-detail-item" style={{ gridColumn: '1 / -1' }}>
              <span className="gdm-detail-label">Notes</span>
              <span className="gdm-detail-value">{order.notes}</span>
            </div>
          )}
        </div>
        <p className="gdm-section-title" style={{ marginTop: 20 }}>Products</p>
        <table className="orders-table" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>Product</th><th>Rate/Unit</th><th>Rate/Box</th>
              <th>Ordered</th><th>Dispatched</th><th>Pending</th>
            </tr>
          </thead>
          <tbody>
            {(order.items || []).map(item => (
              <tr key={item.id}>
                <td style={{ fontWeight: 500 }}>{item.product?.productName || '—'} {item.product?.productAndPacking || '—'}</td>
                <td>₹{item.ratePerUnit}</td>
                <td>₹{item.ratePerBox}</td>
                <td>
                  {item.orderedUnits > 0 && <div>{item.orderedUnits} units</div>}
                  {item.orderedBoxes > 0 && <div>{item.orderedBoxes} boxes</div>}
                </td>
                <td style={{ color: '#1565c0' }}>
                  {item.orderedUnits > 0 && <div>{item.dispatchedUnits || 0} units</div>}
                  {item.orderedBoxes > 0 && <div>{item.dispatchedBoxes || 0} boxes</div>}
                </td>
                <td style={{ color: (item.pendingUnits > 0 || item.pendingBoxes > 0) ? '#e65100' : '#2e7d32', fontWeight: 600 }}>
                  {item.orderedUnits > 0 && <div>{item.pendingUnits || 0} units</div>}
                  {item.orderedBoxes > 0 && <div>{item.pendingBoxes || 0} boxes</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="gdm-modal-footer">
        <button className="gdm-btn-secondary" onClick={onClose}>Close</button>
      </div>
    </div>
  </div>
);

// ── Add Payment Modal ─────────────────────────────────────────────────────────
const AddPaymentModal = ({ order, onClose, onSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState({
    amount: order.totalAmount ? String(order.totalAmount) : '',
    paymentMode: '', paymentDate: new Date().toISOString().split('T')[0],
    referenceNumber: '', remark: '',
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.amount || parseFloat(formData.amount) <= 0) e.amount = 'Enter a valid amount';
    if (!formData.paymentMode) e.paymentMode = 'Payment mode is required';
    if (!formData.paymentDate) e.paymentDate = 'Payment date is required';
    setErrors(e); return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit({
      orderId: order.id, amount: parseFloat(formData.amount),
      paymentMode: formData.paymentMode, paymentDate: formData.paymentDate,
      referenceNumber: formData.referenceNumber || undefined,
      remark: formData.remark || undefined,
    });
  };

  return (
    <div className="gdm-overlay" onClick={onClose}>
      <div className="gdm-modal" onClick={e => e.stopPropagation()}>
        <div className="gdm-modal-header">
          <div>
            <h5 className="gdm-modal-title">Add Payment</h5>
            <p className="gdm-modal-sub">{order.orderId} · {order.dealer?.dealerShopName}</p>
          </div>
          <button className="gdm-close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="gdm-modal-body">
          <div className="gdm-form-row">
            <div className="gdm-form-group">
              <label className="gdm-label">Amount (₹) <span className="required">*</span></label>
              <input type="number" name="amount" className={`gdm-input ${errors.amount ? 'error' : ''}`}
                value={formData.amount} onChange={handleChange} placeholder="Enter amount" min="0" step="0.01" />
              {errors.amount && <span className="gdm-error">{errors.amount}</span>}
            </div>
            <div className="gdm-form-group">
              <label className="gdm-label">Payment Mode <span className="required">*</span></label>
              <select name="paymentMode" className={`gdm-input ${errors.paymentMode ? 'error' : ''}`}
                value={formData.paymentMode} onChange={handleChange}>
                <option value="">Select Mode</option>
                {PAYMENT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              {errors.paymentMode && <span className="gdm-error">{errors.paymentMode}</span>}
            </div>
          </div>
          <div className="gdm-form-row">
            <div className="gdm-form-group">
              <label className="gdm-label">Payment Date <span className="required">*</span></label>
              <input type="date" name="paymentDate" className={`gdm-input ${errors.paymentDate ? 'error' : ''}`}
                value={formData.paymentDate} onChange={handleChange}
                max={new Date().toISOString().split('T')[0]} />
              {errors.paymentDate && <span className="gdm-error">{errors.paymentDate}</span>}
            </div>
            <div className="gdm-form-group">
              <label className="gdm-label">Reference / Transaction No.</label>
              <input type="text" name="referenceNumber" className="gdm-input"
                value={formData.referenceNumber} onChange={handleChange} placeholder="UTR / Cheque / UPI Ref" />
            </div>
          </div>
          <div className="gdm-form-group">
            <label className="gdm-label">Remark</label>
            <textarea name="remark" className="gdm-input" value={formData.remark}
              onChange={handleChange} placeholder="Optional notes" rows={2} style={{ resize: 'vertical' }} />
          </div>
          <div style={{ marginTop: 12, padding: 12, background: '#e8f5e9', borderRadius: 8 }}>
            <p style={{ margin: 0, fontSize: 13, color: '#2e7d32', fontWeight: 600 }}>
              Order Total: {formatAmount(order.totalAmount)}
            </p>
          </div>
        </div>
        <div className="gdm-modal-footer">
          <button className="gdm-btn-secondary" onClick={onClose} disabled={isSubmitting}>Cancel</button>
          <button className="gdm-btn-primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : '✓ Save Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Stock Panel ───────────────────────────────────────────────────────────────
const StockPanel = ({ godownId, setToast }) => {
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchStock = useCallback(async () => {
    setLoading(true);
    try {
      const res = await orderService.getGodownStock(godownId);
      if (res.success) setStock(res.data || []);
    } catch { setToast({ message: 'Failed to fetch stock', type: 'error' }); }
    finally { setLoading(false); }
  }, [godownId]);

  useEffect(() => { if (godownId) fetchStock(); }, [godownId]);

  const handleSave = async (productId) => {
    setIsSubmitting(true);
    try {
      await orderService.updateGodownStock(godownId, {
        productId,
        availableUnits: parseInt(editValues.availableUnits ?? 0),
        availableBoxes: parseInt(editValues.availableBoxes ?? 0),
      });
      setToast({ message: 'Stock updated!', type: 'success' });
      setEditingId(null); fetchStock();
    } catch { setToast({ message: 'Failed to update stock', type: 'error' }); }
    finally { setIsSubmitting(false); }
  };

  if (loading) return <div className="loading">Loading stock...</div>;
  return (
    <div className="table-container">
      {stock.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#adb5bd' }}>No stock records found</div>
      ) : (
        <table className="orders-table">
          <thead><tr><th>Product</th><th>Available Units</th><th>Available Boxes</th><th>Action</th></tr></thead>
          <tbody>
            {stock.map(item => (
              <tr key={item.id}>
                <td style={{ fontWeight: 500 }}>{item.product?.productName || '—'}</td>
                <td>
                  {editingId === item.id
                    ? <input type="number" min="0" style={{ width: 80, padding: '4px 8px', border: '1px solid #dee2e6', borderRadius: 6 }}
                      value={editValues.availableUnits}
                      onChange={e => setEditValues(p => ({ ...p, availableUnits: e.target.value }))} />
                    : <span style={{ fontWeight: 600, color: item.availableUnits > 0 ? '#2e7d32' : '#e53935' }}>{item.availableUnits}</span>
                  }
                </td>
                <td>
                  {editingId === item.id
                    ? <input type="number" min="0" style={{ width: 80, padding: '4px 8px', border: '1px solid #dee2e6', borderRadius: 6 }}
                      value={editValues.availableBoxes}
                      onChange={e => setEditValues(p => ({ ...p, availableBoxes: e.target.value }))} />
                    : <span style={{ fontWeight: 600, color: item.availableBoxes > 0 ? '#2e7d32' : '#e53935' }}>{item.availableBoxes}</span>
                  }
                </td>
                <td>
                  {editingId === item.id
                    ? <div style={{ display: 'flex', gap: 6 }}>
                      <button className="action-btn delivered-btn" onClick={() => handleSave(item.productId)} disabled={isSubmitting}>{isSubmitting ? '...' : '✓ Save'}</button>
                      <button className="action-btn" style={{ background: '#f5f5f5', color: '#495057' }} onClick={() => setEditingId(null)}>Cancel</button>
                    </div>
                    : <button className="action-btn dispatch-btn"
                      onClick={() => { setEditingId(item.id); setEditValues({ availableUnits: item.availableUnits, availableBoxes: item.availableBoxes }); }}>
                      ✏️ Edit
                    </button>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const GodownManagerDashboard = () => {
  const { user } = useAuth();

  // Group godownAccess into distinct (godown, company) contexts
  const godownContexts = useMemo(() => {
    const access = user?.godownAccess || [];
    const grouped = new Map();
    for (const g of access) {
      const key = `${g.godownId}:${g.companyId}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          godownId: g.godownId, godownName: g.godownName, godownLocation: g.godownLocation,
          companyId: g.companyId, companyName: g.companyName,
          regionNames: [],
        });
      }
      grouped.get(key).regionNames.push(g.regionName);
    }
    return Array.from(grouped.values());
  }, [user]);

  const [selectedContext, setSelectedContext] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    const resolve = async () => {
      if (godownContexts.length > 0) {
        setSelectedContext(godownContexts[0]); // default to first; picker lets them switch
        setProfileLoading(false);
        return;
      }
      // Fallback: refetch profile in case AuthContext's user is stale
      try {
        const res = await authService.getProfile();
        const access = res.data?.godownAccess || [];
        if (access.length > 0) {
          const g = access[0];
          setSelectedContext({
            godownId: g.godownId, godownName: g.godownName, godownLocation: g.godownLocation,
            companyId: g.companyId, companyName: g.companyName, regionNames: [g.regionName],
          });
        }
      } catch { }
      finally { setProfileLoading(false); }
    };
    resolve();
  }, [user]);

  const godownId = selectedContext?.godownId || null;
  const companyId = selectedContext?.companyId || null;

  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('ALL');
  const [viewTab, setViewTab] = useState('orders');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [dispatchOrder, setDispatchOrder] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [isPaySubmitting, setIsPaySubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;

  const [confirmDelivery, setConfirmDelivery] = useState({
    open: false,
    orderId: null,
    status: null,
    loading: false,
  });

  const [companyFilter, setCompanyFilter] = useState('ALL');

  useEffect(() => { setCurrentPage(1); setCompanyFilter('ALL'); }, [godownId]);

  const fetchOrders = useCallback(async () => {
    if (!godownId) return;
    setLoading(true);
    try {
      const res = await orderService.getOrdersByGodown(godownId);
      if (res.success) setOrders(res.data || []);
    } catch { setToast({ message: 'Failed to fetch orders', type: 'error' }); }
    finally { setLoading(false); }
  }, [godownId]);

  useEffect(() => { if (viewTab === 'orders') fetchOrders(); }, [viewTab, fetchOrders]);
  useEffect(() => { setCurrentPage(1); }, [activeTab, companyFilter]);

  const companyOptions = useMemo(() => {
    const map = new Map();
    for (const o of orders) {
      const c = o.dealer?.company;
      if (c?.id && !map.has(c.id)) map.set(c.id, c.name);
    }
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [orders]);

  const companyScopedOrders = companyFilter === 'ALL'
    ? orders
    : orders.filter(o => String(o.dealer?.company?.id) === String(companyFilter));


  const countByStatus = (key) => companyScopedOrders.filter(o => o.orderStatus === key).length;

  const filtered = (() => {
    if (activeTab === 'ALL') return companyScopedOrders;

    if (activeTab === 'DELIVERED') {
      return companyScopedOrders.filter(o =>
        o.orderStatus === 'DELIVERED' ||
        (o.orderStatus === 'PARTIALLY_DISPATCHED' && (o.deliveredAt || hasAnyDeliveredItem(o)))
      );
    }

    if (activeTab === 'DISPATCHED') {
      return companyScopedOrders.filter(o =>
        o.orderStatus === 'DISPATCHED' ||
        (o.orderStatus === 'PARTIALLY_DISPATCHED' && !o.deliveredAt)
      );
    }

    return companyScopedOrders.filter(o => o.orderStatus === activeTab);
  })();

  // Helper — true if any item has dispatchedUnits > 0 or dispatchedBoxes > 0
  function hasAnyDeliveredItem(order) {
    return (order.items || []).some(i =>
      (i.dispatchedUnits || 0) > 0 || (i.dispatchedBoxes || 0) > 0
    );
  }

  const handleDispatchSubmit = async (data) => {
    try {
      await orderService.dispatchOrder(dispatchOrder.id, data);
      setToast({ message: 'Dispatched!', type: 'success' });
      setDispatchOrder(null); fetchOrders();
    } catch (err) { setToast({ message: err.response?.data?.message || 'Failed', type: 'error' }); }
  };

  const handleDeliveryUpdate = async (orderId, status) => {
    if (!window.confirm(`Mark as ${status.replace(/_/g, ' ')}?`)) return;
    try {
      await orderService.updateDeliveryStatus(orderId, { status });
      setToast({ message: 'Status updated!', type: 'success' }); fetchOrders();
    } catch { setToast({ message: 'Failed', type: 'error' }); }
  };

  const askDeliveryUpdate = (orderId, status) => {
    setConfirmDelivery({ open: true, orderId, status, loading: false });
  };

  const confirmDeliveryUpdate = async () => {
    setConfirmDelivery(prev => ({ ...prev, loading: true }));
    try {
      await orderService.updateDeliveryStatus(confirmDelivery.orderId, {
        status: confirmDelivery.status,
      });
      setToast({ message: 'Status updated!', type: 'success' });
      fetchOrders();
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to update',
        type: 'error',
      });
    } finally {
      setConfirmDelivery({ open: false, orderId: null, status: null, loading: false });
    }
  };

  const cancelDeliveryUpdate = () => {
    if (confirmDelivery.loading) return;
    setConfirmDelivery({ open: false, orderId: null, status: null, loading: false });
  };

  const handleAddPayment = async (data) => {
    setIsPaySubmitting(true);
    try {
      const res = await paymentService.createPayment(data);
      if (res.success) {
        setToast({ message: 'Payment entry added!', type: 'success' });
        setPaymentOrder(null); fetchOrders();
      }
    } catch (err) { setToast({ message: err.response?.data?.message || 'Failed', type: 'error' }); }
    finally { setIsPaySubmitting(false); }
  };

  const handleExport = () => {
    if (!filtered.length) return;
    const rows = [['Order ID', 'Date', 'Dealer', 'Items', 'Total', 'Status'],
    ...filtered.map(o => [o.orderId, formatDate(o.orderDate), o.dealer?.dealerShopName || '', o.items?.length || 0, o.totalAmount, o.orderStatus])
    ].map(r => r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([rows], { type: 'text/csv' }));
    a.download = `orders-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginatedOrders = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  if (profileLoading) return (
    <div className="godown-dashboard">
      <div style={{ padding: 60, textAlign: 'center', color: '#6c757d' }}>
        Loading dashboard...
      </div>
    </div>
  );

  if (!godownId) return (
    <div className="godown-dashboard">
      <div style={{ padding: 60, textAlign: 'center', color: '#e53935' }}>
        <p style={{ fontSize: 40 }}>⚠️</p>
        <p style={{ fontSize: 16, fontWeight: 600 }}>No godown assigned to your account.</p>
        <p style={{ color: '#6c757d' }}>Please contact your administrator to assign a godown.</p>
      </div>
    </div>
  );

  return (
    <div className="godown-dashboard">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="dashboard-header">
        <div>
          <h4 className="dashboard-title">Godown Manager Dashboard</h4>
          <p style={{ fontSize: 13, color: '#6c757d', margin: '2px 0 0 0' }}>Godown ID: <strong>{godownId}</strong></p>
        </div>
        {viewTab === 'orders' && (
          <button className="gdm-export-btn" onClick={handleExport} disabled={!filtered.length}>⬇ Export CSV</button>
        )}
      </div>
      {(godownContexts.length > 1 || companyOptions.length > 1) && (
        <div className="gdm-context-bar">
          {godownContexts.length > 1 && (
            <div className="gdm-select-group">
              <span className="gdm-select-label">Godown & Company</span>
              <select
                className="gdm-context-select"
                value={`${selectedContext?.godownId}:${selectedContext?.companyId}`}
                onChange={(e) => {
                  const [gId, cId] = e.target.value.split(':').map(Number);
                  setSelectedContext(godownContexts.find(c => c.godownId === gId && c.companyId === cId));
                }}
              >
                {godownContexts.map(c => (
                  <option key={`${c.godownId}:${c.companyId}`} value={`${c.godownId}:${c.companyId}`}>
                    {c.godownName} — {c.companyName} ({c.regionNames.join(', ')})
                  </option>
                ))}
              </select>
            </div>
          )}

          {companyOptions.length > 1 && (
            <div className="gdm-select-group">
              <span className="gdm-select-label">Filter by Company</span>
              <select
                className="gdm-context-select"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
              >
                <option value="ALL">All Companies</option>
                {companyOptions.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Summary — clickable to filter */}
      <div className="godown-summary-cards">
        {[
          { label: 'Pending', key: 'PENDING_CONFIRMATION', color: '#f57f17', bg: '#fff8e1' },
          { label: 'Confirmed', key: 'CONFIRMED', color: '#2e7d32', bg: '#e8f5e9' },
          { label: 'Partial', key: 'PARTIALLY_DISPATCHED', color: '#e65100', bg: '#fff3e0' },
          { label: 'Dispatched', key: 'DISPATCHED', color: '#1565c0', bg: '#e3f2fd' },
          { label: 'Delivered', key: 'DELIVERED', color: '#1b5e20', bg: '#c8e6c9' },
        ].map(c => (
          <div key={c.key} className="godown-summary-card"
            style={{ background: c.bg, borderLeft: `4px solid ${c.color}`, cursor: 'pointer' }}
            onClick={() => { setViewTab('orders'); setActiveTab(c.key); }}>
            <p className="gsc-count" style={{ color: c.color }}>{countByStatus(c.key)}</p>
            <p className="gsc-label">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="view-tabs">
        {[{ key: 'orders', label: '📦 Orders' }, { key: 'stock', label: '🏭 Stock' }].map(t => (
          <button key={t.key} className={`view-tab ${viewTab === t.key ? 'active' : ''}`} onClick={() => setViewTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {viewTab === 'stock' ? <StockPanel godownId={godownId} setToast={setToast} /> : (
        <>
          <div className="tabs-container">
            {STATUS_TABS.map(tab => (
              <button key={tab.key} className={`tab ${activeTab === tab.key ? 'active' : ''}`} onClick={() => setActiveTab(tab.key)}>
                {tab.label}

              </button>
            ))}
          </div>

          <div className="table-container">
            {loading ? <div className="loading">Loading orders...</div>
              : filtered.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: '#adb5bd' }}>📦 No orders found</div>
                : (
                  <table className="orders-table">
                    <thead>
                      <tr><th>#</th><th>Order ID</th><th>Date</th><th>Dealer</th><th>Company</th><th>Items</th><th>Total</th><th>Status</th><th>Progress</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {paginatedOrders.map((order, idx) => {
                        const style = STATUS_COLORS[order.orderStatus] || {};
                        const canDispatch = ['CONFIRMED', 'PARTIALLY_DISPATCHED'].includes(order.orderStatus);
                        const canDeliver = order.orderStatus === 'DISPATCHED';
                        const canPay = order.orderStatus === 'DELIVERED';
                        return (
                          <tr key={order.id}>
                            <td>{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                            <td className="order-id-cell">{order.orderId}</td>
                            <td>{formatDate(order.orderDate)}</td>
                            <td>
                              {order.dealer?.dealerShopName || '—'}<br />
                              {order.dealer?.thasil || ''}<br />
                              {order.dealer?.district || ''}<br />
                              {order.dealer?.state || ''}
                            </td>
                            <td>{order.dealer?.company?.name || '—'}</td>
                            <td><span className="order-items-badge">{order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}</span></td>
                            <td style={{ fontWeight: 600 }}>{formatAmount(order.totalAmount)}</td>
                            <td>
                              <span className="status-badge" style={{ background: style.bg, color: style.color, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                                {order.orderStatus === 'PARTIALLY_DISPATCHED' && order.deliveredAt
                                  ? 'PARTIAL · DELIVERED'
                                  : order.orderStatus?.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td>
                              {order.items?.map(item => (
                                <div key={item.id} style={{ fontSize: 12, marginBottom: 2 }}>
                                  <span style={{ color: '#495057' }}>{item.product?.productName?.slice(0, 12)}:</span>
                                  {item.orderedUnits > 0 && <span style={{ color: item.pendingUnits > 0 ? '#e65100' : '#2e7d32', marginLeft: 4 }}>{item.dispatchedUnits}/{item.orderedUnits}u</span>}
                                  {item.orderedBoxes > 0 && <span style={{ color: item.pendingBoxes > 0 ? '#e65100' : '#2e7d32', marginLeft: 4 }}>{item.dispatchedBoxes}/{item.orderedBoxes}b</span>}
                                </div>
                              ))}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                <button style={{ background: '#e3f2fd', color: '#1565c0', border: 'none', padding: '4px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}
                                  onClick={() => setDetailOrder(order)}>👁 View</button>
                                {canDispatch && (
                                  <button
                                    style={{
                                      background: '#fff3e0', color: '#e65100', border: '1px solid #e65100',
                                      padding: '4px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600
                                    }}
                                    onClick={() => setDispatchOrder(order)}
                                  >
                                    🚚 Dispatch
                                  </button>
                                )}
                                {canDeliver && (<>
                                  <button
                                    style={{ background: '#e8f5e9', color: '#2e7d32', border: '1px solid #2e7d32', padding: '4px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                                    onClick={() => askDeliveryUpdate(order.id, 'DELIVERED')}
                                  >✓ Delivered</button>
                                  <button
                                    style={{ background: '#fce4ec', color: '#c62828', border: '1px solid #c62828', padding: '4px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                                    onClick={() => askDeliveryUpdate(order.id, 'FAILED_TO_DELIVER')}
                                  >✕ Failed</button>
                                </>)}
                                {canPay && (
                                  <button style={{ background: '#e8f5e9', color: '#2e7d32', border: 'none', padding: '4px 8px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}
                                    onClick={() => setPaymentOrder(order)}>💰 Payment</button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
            {filtered.length > PAGE_SIZE && (
              <div style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                gap: 12, padding: '16px 0',
              }}>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 14px', borderRadius: 6, border: '1px solid #dee2e6',
                    background: currentPage === 1 ? '#f5f5f5' : '#fff',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: 13,
                  }}
                >
                  ← Prev
                </button>
                <span style={{ fontSize: 13, color: '#495057' }}>
                  Page {currentPage} of {totalPages} · {filtered.length} orders
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '6px 14px', borderRadius: 6, border: '1px solid #dee2e6',
                    background: currentPage === totalPages ? '#f5f5f5' : '#fff',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: 13,
                  }}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {dispatchOrder && <DispatchModal order={dispatchOrder} onClose={() => setDispatchOrder(null)} onSubmit={handleDispatchSubmit} />}
      {detailOrder && <OrderDetailModal order={detailOrder} onClose={() => setDetailOrder(null)} />}
      {paymentOrder && <AddPaymentModal order={paymentOrder} onClose={() => setPaymentOrder(null)} onSubmit={handleAddPayment} isSubmitting={isPaySubmitting} />}
      {confirmDelivery.open && (
        <div className="gdm-overlay" onClick={cancelDeliveryUpdate}>
          <div className="gdm-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="gdm-modal-header">
              <div>
                <h5 className="gdm-modal-title">
                  {confirmDelivery.status === 'DELIVERED'
                    ? '✓ Mark as Delivered'
                    : '✕ Mark as Failed'}
                </h5>
                <p className="gdm-modal-sub">
                  {confirmDelivery.status === 'DELIVERED'
                    ? 'Confirm successful delivery. A payment entry will be auto-created.'
                    : 'Confirm the order failed to deliver.'}
                </p>
              </div>
              <button
                className="gdm-close-btn"
                onClick={cancelDeliveryUpdate}
                disabled={confirmDelivery.loading}
              >✕</button>
            </div>

            <div className="gdm-modal-footer">
              <button
                className="gdm-btn-secondary"
                onClick={cancelDeliveryUpdate}
                disabled={confirmDelivery.loading}
              >Cancel</button>
              <button
                className="gdm-btn-primary"
                onClick={confirmDeliveryUpdate}
                disabled={confirmDelivery.loading}
                style={confirmDelivery.status === 'FAILED_TO_DELIVER'
                  ? { background: '#c62828' }
                  : undefined}
              >
                {confirmDelivery.loading ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GodownManagerDashboard;